import React, { useState, useRef, useEffect } from 'react';
import { IconChevronDown } from '@tabler/icons-react';
import { useMasterData } from '../context/MasterDataContext';

/**
 * Reusable Karat Checkbox Dropdown.
 * Uses dynamic karatList from MasterDataContext.
 *
 * @param {string|Array} selectedKarat - Currently selected karat ('16,18' or ['16', '18'])
 * @param {Function} onChange - Callback function receiving comma-separated string (e.g. '16,18' or '')
 * @param {string} variant - 'standard' (default, 40px height for forms/filter bars) or 'compact' (for summary cards)
 * @param {string} className - Optional wrapper class
 */
function KaratCheckboxDropdown({
  selectedKarat = '',
  onChange,
  variant = 'standard',
  className = '',
}) {
  const { karatList = [] } = useMasterData() || {};
  const karatOptions =
    karatList && karatList.length > 0
      ? karatList
      : [16, 17, 18, 19, 20, 21, 22, 23, 24];

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Convert selectedKarat prop to array of strings
  const selectedArray = React.useMemo(() => {
    if (!selectedKarat) return [];
    if (Array.isArray(selectedKarat)) return selectedKarat.map(String);
    return String(selectedKarat)
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }, [selectedKarat]);

  // Click outside listener
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // ESC key listener
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleToggle = (karat) => {
    const kStr = String(karat);
    let newSelected;
    if (selectedArray.includes(kStr)) {
      newSelected = selectedArray.filter((k) => k !== kStr);
    } else {
      newSelected = [...selectedArray, kStr];
    }
    if (onChange) {
      onChange(newSelected.join(','));
    }
  };

  const handleReset = () => {
    if (onChange) {
      onChange('');
    }
  };

  const isCompact = variant === 'compact';

  const displayText = () => {
    if (isCompact) {
      if (selectedArray.length === 0) return 'Semua';
      if (selectedArray.length === 1) return `${selectedArray[0]}k`;
      return `${selectedArray.length} Karat`;
    }
    if (selectedArray.length === 0) return 'Karat';
    return `Karat (${selectedArray.length})`;
  };

  return (
    <div className={`relative inline-block text-left ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={
          isCompact
            ? 'flex items-center gap-1.5 bg-white dark:bg-slate-800 px-2 py-1 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm cursor-pointer hover:border-amber-300 dark:hover:border-amber-500 transition-colors text-xs font-bold text-slate-700 dark:text-slate-200 outline-none'
            : 'flex items-center justify-between gap-2 h-[2.5rem] px-3 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm rounded-md cursor-pointer hover:border-amber-500 focus:outline-none transition-colors shadow-sm min-w-[110px]'
        }
      >
        {isCompact ? (
          <>
            <span className="text-[10px] font-bold text-slate-400 uppercase">Karat:</span>
            <span className={selectedArray.length > 0 ? 'text-amber-600 dark:text-amber-400 font-bold' : ''}>
              {displayText()}
            </span>
          </>
        ) : (
          <span className={selectedArray.length > 0 ? 'text-amber-600 dark:text-amber-400 font-semibold' : ''}>
            {displayText()}
          </span>
        )}
        <IconChevronDown
          size={isCompact ? 14 : 16}
          className={`text-slate-400 dark:text-slate-400 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute left-0 sm:right-auto mt-1.5 w-52 p-3 shadow-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl z-[9999]">
          <div className="flex items-center justify-between font-bold text-xs text-slate-500 dark:text-slate-400 mb-2 border-b dark:border-slate-700 pb-1.5 uppercase tracking-wider">
            <span>Pilih Karat</span>
            {selectedArray.length > 0 && (
              <button
                type="button"
                onClick={handleReset}
                className="text-xs text-rose-500 hover:text-rose-600 font-bold normal-case hover:underline transition-colors"
              >
                Reset
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-1.5 max-h-52 overflow-y-auto pr-1">
            {karatOptions.map((kto) => {
              const ktoStr = String(kto);
              const isChecked = selectedArray.includes(ktoStr);
              return (
                <label
                  key={kto}
                  className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 p-1.5 rounded-lg transition-colors"
                >
                  <input
                    type="checkbox"
                    className="checkbox checkbox-xs checkbox-warning rounded"
                    checked={isChecked}
                    onChange={() => handleToggle(kto)}
                  />
                  <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                    {kto}k
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default KaratCheckboxDropdown;
